import { Injectable } from '@angular/core';
import { BehaviorSubject, map, forkJoin, take } from 'rxjs';
import { SocketService } from '../../../core/services/socket.service';
import { ContactsService } from '../../widgets/contacts/contacts.service';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';

export interface Story {
  name: string;
  image: string;
}

export interface Chat {
  name: string;
  image: string;
  message: string;
  time: string;
  online: boolean;
  active: boolean;
  userId?: string;
}

export interface Message {
  id?: number;
  text?: string;
  image?: string;
  voice?: boolean;
  sender: 'them' | 'me';
  time?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChatDataService {
  private _stories = new BehaviorSubject<Story[]>([]);
  stories$ = this._stories.asObservable();

  private _chats = new BehaviorSubject<Chat[]>([]);
  chats$ = this._chats.asObservable();

  activeChat$ = this.chats$.pipe(
    map((chats) => chats.find((chat) => chat.active) || null),
  );

  private _messages = new BehaviorSubject<Message[]>([]);
  messages$ = this._messages.asObservable();
  private sessionMessages: Record<string, Message[]> = {};

  private _typingStatus = new BehaviorSubject<{
    userId: string;
    isTyping: boolean;
  } | null>(null);
  typingStatus$ = this._typingStatus.asObservable();

  /**
   * ════════════════════════════════════════════════════════════════════════════════
   * BOT ABSTRACTION LAYER - EASY SWITCHING BETWEEN BOT AND REAL USERS
   * ════════════════════════════════════════════════════════════════════════════════
   *
   * This flag controls whether the app uses a teaching bot or real backend users
   * Set to TRUE for development with dummy bot responses
   * Set to FALSE (or remove entirely) when integrating real backend
   *
   * The backend will naturally replace the bot layer without code changes
   * ════════════════════════════════════════════════════════════════════════════════
   */
  private USE_BOT_RESPONSES = false; // ← EASY TOGGLE: true = bot, false = real people

  constructor(
    private socketService: SocketService,
    private contactsService: ContactsService,
    private apiService: ApiService,
    private authService: AuthService,
  ) {
    this.socketService.messageFromSocket$.subscribe((msg: any) => {
      if (msg) {
        this.addIncomingMessage(msg);
      }
    });
    this.socketService.userTyping$.subscribe((status: any) => {
      if (status) {
        this._typingStatus.next(status);
      }
    });
    this.loadInitialChats();
  }

  private loadInitialChats() {
    this.fetchChats();
    // Dynamic update every 10 seconds
    setInterval(() => {
      this.fetchChats();
    }, 10000);
  }

  public fetchChats() {
    if (!this.authService.token) {
      console.log('ChatDataService: No token found, skipping fetchChats');
      return;
    }
    forkJoin({
      recent: this.apiService.chatRecent(),
      contacts: this.apiService.contactsGet(),
    }).subscribe({
      next: (res: any) => {
        const recentData = res.recent?.data || [];
        const contactsData = res.contacts?.data || [];
        const currentActive = this._chats.getValue().find((c) => c.active);

        // Map recent chats
        const recentChats: Chat[] = recentData.map((item: any) => {
          const id = item.userId || item._id;
          const sessionMsgs = this.sessionMessages[id] || [];
          const lastSessionMsg = sessionMsgs[sessionMsgs.length - 1];
          return {
            name: item.name,
            image: item.profileImage || 'assets/images/profile.png',
            message: lastSessionMsg ? lastSessionMsg.text : (item.lastMessage?.text || 'Say hello!'),
            time: lastSessionMsg ? 'Now' : (item.lastMessage?.time || 'Recently'),
            online: true,
            active: currentActive?.userId === id,
            userId: id,
          };
        });

        // Map contacts that aren't in recent chats
        const contactChats: Chat[] = contactsData
          .filter((contact: any) => {
            const contactId = contact.userId || contact._id;
            return !recentChats.find((r) => r.userId === contactId);
          })
          .map((contact: any) => {
            const id = contact.userId || contact._id;
            return {
              name: contact.name,
              image: contact.profileImage || 'assets/images/avatar-jane.jpg',
              message: 'Say hello!',
              time: contact.is_online ? 'Online' : 'Offline',
              online: contact.is_online || false,
              active: currentActive?.userId === id,
              userId: id,
            };
          });

        // Always include Orbital Dummies for development/bot testing
        const allChats = [...recentChats, ...contactChats];

        // Final uniqueness safety check
        const uniqueChats: Chat[] = [];
        const seenIds = new Set();
        allChats.forEach((chat) => {
          const id = chat.userId || chat.name; // Fallback to name if ID missing
          if (!seenIds.has(id)) {
            seenIds.add(id);
            uniqueChats.push(chat);
          }
        });

        this._chats.next(uniqueChats);

        // Update stories from unique chats
        if (allChats.length > 0) {
          this._stories.next(
            allChats.slice(0, 8).map((c) => ({ name: c.name, image: c.image })),
          );
        }
      },
      error: (error: any) => {
        console.error(
          'ChatDataService: Error fetching chats, keeping cached contacts:',
          error,
        );
        // Don't clear existing contacts on transient errors — keep cached data visible
      },
    });
  }

  addIncomingMessage(msg: any) {
    console.log('addIncomingMessage called with:', msg);
    const text = msg.message || msg.text || msg;
    const fromUserId = msg.fromUserId || msg.from || msg.userId;
    const voice = msg.type === 'voice' || msg.voice === true || (text && text.startsWith('data:audio/'));

    // Clear typing status when message arrives
    this._typingStatus.next({ userId: fromUserId, isTyping: false });

    // Update message history if active
    this.activeChat$.pipe(take(1)).subscribe((active) => {
      const activeId = active?.userId;
      console.log(
        'Checking active chat:',
        activeId,
        'against fromUserId:',
        fromUserId,
      );
      if (activeId && activeId === fromUserId) {
        console.log('ID Match! Adding message to screen.');
        const currentMessages = this._messages.getValue();
        const newMessage: Message = {
          text,
          sender: 'them',
          voice,
        };
        this._messages.next([...currentMessages, newMessage]);
      } else {
        console.log('ID Mismatch or no active chat. ID comparison failed.');
      }

      // Always save to session cache even if not active
      if (!this.sessionMessages[fromUserId]) this.sessionMessages[fromUserId] = [];
      this.sessionMessages[fromUserId].push({
        text,
        sender: 'them',
        voice,
      });
    });

    // Update sidebar preview
    const currentChats = this._chats.getValue();
    const chatIndex = currentChats.findIndex((c) => c.userId === fromUserId);
    if (chatIndex > -1) {
      const updatedChat = {
        ...currentChats[chatIndex],
        message: voice ? 'Voice message' : text,
        time: 'Now',
      };
      const newChats = [...currentChats];
      newChats[chatIndex] = updatedChat;
      this._chats.next(newChats);
    } else {
      // If new chat, fetch all to be sure
      this.fetchChats();
    }
  }

  /**
   * ════════════════════════════════════════════════════════════════════════════════
   * MAIN SEND MESSAGE METHOD
   *
   * This method now intelligently routes to either BOT or REAL BACKEND:
   *   1. Message always saved to database via API
   *   2. Message always sent via socket to backend
   *   3. If USE_BOT_RESPONSES = true, bot responds (development/testing)
   *   4. If USE_BOT_RESPONSES = false, backend handles everything (production)
   *
   * When client integrates real backend:
   *   - Simply set USE_BOT_RESPONSES = false
   *   - Backend will respond to emitChatMessage() socket event
   *   - No other code needs to change!
   * ════════════════════════════════════════════════════════════════════════════════
   */
  sendMessage(text: string, toUserId: string, voice = false) {
    const currentMessages = this._messages.getValue();
    const newMessage: Message = {
      text,
      sender: 'me',
      voice,
    };
    this._messages.next([...currentMessages, newMessage]);

    // Update session cache
    if (!this.sessionMessages[toUserId]) this.sessionMessages[toUserId] = [];
    this.sessionMessages[toUserId].push(newMessage);

    // Update sidebar preview
    const currentChats = this._chats.getValue();
    const chatIndex = currentChats.findIndex((c) => c.userId === toUserId);
    if (chatIndex > -1) {
      const updatedChat = {
        ...currentChats[chatIndex],
        message: voice ? 'Voice message' : text,
        time: 'Now',
      };
      const newChats = [...currentChats];
      newChats[chatIndex] = updatedChat;
      this._chats.next(newChats);
    }

    // Backend persistence enabled
    this.apiService
      .chatStore({
        userId: toUserId,
        text: text,
      })
      .subscribe(
        (res) => console.log('Message persisted to backend:', res),
        (err) => console.error('Failed to persist message:', err),
      );

    // ════════════════════════════════════════════════════════════════════════════════
    // BACKEND INTEGRATION POINT #2: Send via WebSocket
    // ════════════════════════════════════════════════════════════════════════════════
    // The backend socket listens on 'chatMessage' and broadcasts to recipient
    // Backend should emit 'incomingMessage' to the recipient's socket
    this.socketService.emitChatMessage(toUserId, text, voice ? 'voice' : 'text');

    // ════════════════════════════════════════════════════════════════════════════════
    // BOT ABSTRACTION LAYER: Lightweight teaching bot for development
    // ════════════════════════════════════════════════════════════════════════════════
    // When USE_BOT_RESPONSES = true, simulate a bot reply for teaching purposes
    // When USE_BOT_RESPONSES = false, the real backend handles responses
    if (this.USE_BOT_RESPONSES) {
      const botName = currentChats[chatIndex]?.name || 'Contact';
      this.simulateBotResponse(text.toLowerCase(), toUserId, botName);
    }
  }

  /**
   * ════════════════════════════════════════════════════════════════════════════════
   * BOT RESPONSE SIMULATION - Can be easily disabled
   *
   * This method provides realistic response messaging for development
   * It simulates typing indicators and message delays
   * When backend integration is complete, this is never called (USE_BOT_RESPONSES = false)
   * ════════════════════════════════════════════════════════════════════════════════
   */
  private simulateBotResponse(
    input: string,
    toUserId: string,
    botName: string,
  ) {
    let reply = '';

    // Realistic bot responses based on keywords
    if (
      input.includes('hi') ||
      input.includes('hello') ||
      input.includes('hey')
    ) {
      reply = `Hey! How are you doing?`;
    } else if (input.includes('how are you') || input.includes('whats up')) {
      reply = `I'm doing great, thanks for asking! How about you?`;
    } else if (input.includes('status') || input.includes('any updates')) {
      reply = `All systems operational. Everything is running smoothly!`;
    } else if (input.includes('help') || input.includes('support')) {
      reply = `I'm here to help! What do you need?`;
    } else if (input.includes('thanks') || input.includes('thank you')) {
      reply = `You're welcome! Happy to assist anytime.`;
    } else if (input.includes('bye') || input.includes('goodbye')) {
      reply = `See you later! Take care!`;
    } else if (input.includes('?')) {
      reply = `That's an interesting question. Let me think about that...`;
    } else {
      reply = `Got it! I'll look into that for you.`;
    }

    // Wait 2s before typing indicator starts
    setTimeout(() => {
      this._typingStatus.next({ userId: toUserId, isTyping: true });

      // Bot "types" for 2-3 seconds before sending
      setTimeout(
        () => {
          this._typingStatus.next({ userId: toUserId, isTyping: false });

          // Re-verify we are still in the same chat
          this.activeChat$.pipe(take(1)).subscribe((active) => {
            const newMessage: Message = {
              text: reply,
              sender: 'them',
              time: new Date().toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              }),
            };

            // Save to session cache
            if (!this.sessionMessages[toUserId])
              this.sessionMessages[toUserId] = [];
            this.sessionMessages[toUserId].push(newMessage);

            if (active?.userId === toUserId) {
              const current = this._messages.getValue();
              this._messages.next([...current, newMessage]);
            }
          });
        },
        2000 + Math.random() * 1000,
      );
    }, 2000);
  }

  addChat(user: any) {
    const currentChats = this._chats.getValue();
    const userId = user.userId || user._id;
    const existingIndex = currentChats.findIndex((c) => c.userId === userId);

    let newChat: Chat;
    if (existingIndex > -1) {
      newChat = { ...currentChats[existingIndex], active: true };
    } else {
      newChat = {
        name: user.name,
        image: user.profileImage || 'assets/images/avatar-jane.jpg',
        message: 'Just added you as a contact!',
        time: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
        online: true,
        active: true,
        userId: userId,
      };
    }

    // Deactivate others and move new/existing to top
    const otherChats = currentChats
      .filter((_, i) => i !== existingIndex)
      .map((c) => ({ ...c, active: false }));

    this._chats.next([newChat, ...otherChats]);
    this.setActiveChat(newChat.userId);
  }

  setActiveChat(id: string | undefined | null) {
    const currentChats = this._chats.getValue();
    const updatedChats = currentChats.map((chat) => ({
      ...chat,
      active: chat.userId === id,
    }));
    this._chats.next(updatedChats);

    if (id) {
      this.loadMessageHistory(id);
    }
  }

  private loadMessageHistory(userId: string) {
    this.apiService.chatGet(userId).subscribe((res: any) => {
      const currentUserId = this.authService.user?._id || 'user_123';
      const history: Message[] = res.data
        ? res.data.map((m: any) => {
            const fromId = m.from || m.fromUserId;
            return {
              text: m.text,
              sender: fromId === currentUserId ? 'me' : 'them',
              voice: m.type === 'voice' || (m.text && m.text.startsWith('data:audio/')),
              time: m.timestamp
                ? new Date(m.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'Now',
            };
          })
        : [];

      // Combine backend history with ephemeral session messages
      const sessionHistory = this.sessionMessages[userId] || [];
      this._messages.next([...history, ...sessionHistory]);
    });
  }
}
